import React, { useEffect, useState } from 'react';
import { Card, Col, Row, Switch, Table, Tag, Typography, Spin, Tabs, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { disableRefetch } from '../../redux/slices/menu';
import { useNavigate } from 'react-router-dom';
import paymentService from '../../services/payment';
import axios from 'axios';
import { api_url } from '../../configs/app-global';

const { Title } = Typography;
const { TabPane } = Tabs;

export default function PaymentModes() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [paymentModes, setPaymentModes] = useState([]);
    const [toggleLoading, setToggleLoading] = useState({});
    const { activeMenu } = useSelector((state) => state.menu, shallowEqual);
    const [customToken, setCustomTokenState] = useState(null);
    const setCustomToken = (token) => {
        if (token) {
            localStorage.setItem('token', token);
            setCustomTokenState(token);
            return true;
        }
        return false;
    };

    // Function to get the current auth token with priority for custom token
    const getAuthToken = () => {
        if (customToken) {
            return customToken;
        }
        return localStorage.getItem('token');
    };

    const fetchPaymentModes = async (customTokenParam = null) => {
        setLoading(true);
        try {
            if (customTokenParam) {
                setCustomToken(customTokenParam);
            }

            const token = getAuthToken();
            if (!token) {
                toast.error('Authentication token not found. Please login again.');
                setTimeout(() => {
                    navigate('/login');
                }, 2000);
                return;
            }

            const response = await axios.get(
                `${api_url}dashboard/admin/payments-mode`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    }
                }
            );

            if (Array.isArray(response.data)) {
                setPaymentModes(response.data);
            } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
                setPaymentModes(response.data.data);
            } else if (response.data && response.data.status === false) {
                toast.error(response.data.message || 'Failed to fetch payment modes');

                if (response.data.message === 'User is not logged in.') {
                    localStorage.removeItem('token');
                    setCustomTokenState(null);
                    setTimeout(() => {
                        navigate('/login');
                    }, 2000);
                }
            } else {
                toast.error('Invalid response format or no payment modes available');
            }
        } catch (error) {
            if (error.response) {
                toast.error(`API Error ${error.response.status}: ${error.response.data?.message || 'Failed to fetch payment modes'}`);
            } else if (error.request) {
                toast.error('No response from server. Please check your network connection.');
            } else {
                toast.error(`Error: ${error.message}`);
            }

            // Handle authentication errors
            if (error.response?.status === 401 ||
                error.response?.data?.message === 'User is not logged in.') {
                localStorage.removeItem('token'); // Clear invalid token
                setCustomTokenState(null);
                setTimeout(() => {
                    navigate('/login');
                }, 2000);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // Fetch payment modes using the authenticated token from localStorage
        fetchPaymentModes();
    }, []);

    useEffect(() => {
        if (activeMenu.refetch) {
            fetchPaymentModes();
            dispatch(disableRefetch(activeMenu));
        }
    }, [activeMenu.refetch]);

    const handleTogglePaymentMethod = async (id, method, currentValue) => {
        const toggleId = `${id}-${method}`;
        setToggleLoading(prev => ({ ...prev, [toggleId]: true }));

        try {
            // Get the token (either custom or from localStorage)
            const token = getAuthToken();
            if (!token) {
                toast.error('Authentication token not found. Please login again.');
                setTimeout(() => {
                    navigate('/login');
                }, 2000);
                return;
            }

            // Use direct axios call to match the curl command format exactly
            const response = await axios.post(
                `${api_url}dashboard/admin/payments-mode/toggle/${id}`,
                { field: method },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    }
                }
            );

            if (response.data?.message && response.data?.data) {
                toast.success(response.data.message || `Payment method updated successfully`);

                // Update the local state with the toggled value from the response
                setPaymentModes(prevModes =>
                    prevModes.map(mode =>
                        mode.id === id ? response.data.data : mode
                    )
                );
            } else if (response.data && response.data.status === false) {
                toast.error(response.data.message || 'Failed to update payment method');

                // If user is not logged in, redirect to login
                if (response.data.message === 'User is not logged in.') {
                    localStorage.removeItem('token'); // Clear invalid token
                    setCustomTokenState(null);
                    setTimeout(() => {
                        navigate('/login');
                    }, 2000);
                }
            } else {
                toast.warning('Unexpected response format. Refreshing data...');
                fetchPaymentModes(); // Refresh data in case of unexpected response format
            }
        } catch (error) {
            if (error.response) {
                toast.error(`API Error ${error.response.status}: ${error.response.data?.message || 'Failed to update payment method'}`);
            } else if (error.request) {
                toast.error('No response from server. Please check your network connection.');
            } else {
                toast.error(`Error: ${error.message}`);
            }

            // Handle authentication errors
            if (error.response?.status === 401 ||
                error.response?.data?.message === 'User is not logged in.') {
                localStorage.removeItem('token'); // Clear invalid token
                setCustomTokenState(null);
                setTimeout(() => {
                    navigate('/login');
                }, 2000);
            }
        } finally {
            setToggleLoading(prev => ({ ...prev, [toggleId]: false }));
        }
    };

    const renderPaymentModeCard = (mode) => {
        const modeTitle = mode.mode_for.charAt(0).toUpperCase() + mode.mode_for.slice(1);
        const modeColor =
            mode.mode_for === 'prepaid' ? 'green' :
                mode.mode_for === 'postpaid' ? 'blue' :
                    'purple';

        return (
            <Card
                title={
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Tag color={modeColor} style={{ marginRight: 8 }}>{modeTitle}</Tag>
                        <span>{t(`payment.mode.${mode.mode_for}`)}</span>
                    </div>
                }
                style={{ marginBottom: 16 }}
                key={mode.id}
            >
                <Row gutter={[16, 16]}>
                    <Col span={6}>
                        <Card size="small" title={t('UPI Payment')} bordered={false}
                        style={{border:"1px solid #4d5b75", borderRadius: 4 }} >
                            <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0',}}>
                                <Switch
                                    checked={mode.mode_upi === "1"}
                                    loading={toggleLoading[`${mode.id}-mode_upi`]}
                                    onChange={() => handleTogglePaymentMethod(mode.id, 'mode_upi', mode.mode_upi)}
                                />
                            </div>
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card size="small" title={t('Wallet Payment')}  bordered={false}
                        style={{border:"1px solid #4d5b75", borderRadius: 4 }} >
                            <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}>
                                <Switch
                                    checked={mode.mode_wallet === "1"}
                                    loading={toggleLoading[`${mode.id}-mode_wallet`]}
                                    onChange={() => handleTogglePaymentMethod(mode.id, 'mode_wallet', mode.mode_wallet)}
                                />
                            </div>
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card size="small" title={t('Card Payment')}  bordered={false}
                        style={{border:"1px solid #4d5b75", borderRadius: 4 }} >
                            <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}>
                                <Switch
                                    checked={mode.mode_card === "1"}
                                    loading={toggleLoading[`${mode.id}-mode_card`]}
                                    onChange={() => handleTogglePaymentMethod(mode.id, 'mode_card', mode.mode_card)}
                                />
                            </div>
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card size="small" title={t('Net Banking')}  bordered={false}
                        style={{border:"1px solid #4d5b75", borderRadius: 4 }} >
                            <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}>
                                <Switch
                                    checked={mode.mode_net === "1"}
                                    loading={toggleLoading[`${mode.id}-mode_net`]}
                                    onChange={() => handleTogglePaymentMethod(mode.id, 'mode_net', mode.mode_net)}
                                />
                            </div>
                        </Card>
                    </Col>
                </Row>
            </Card>
        );
    };

    return (
        <Card title={t('Payment Settings')}>
            {loading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                    <Spin size="large" />
                </div>
            ) : (
                <div>
                    <Title level={4}>{t('Payment Methods Configuration')}</Title>
                    <p>{t('Enable or disable payment methods for different transaction types')}</p>

                    {paymentModes.map(mode => renderPaymentModeCard(mode))}

                    {paymentModes.length === 0 && (
                        <div style={{ textAlign: 'center', padding: '40px 0' }}>
                            <p>{t('No payment modes found')}</p>
                        </div>
                    )}
                </div>
            )}
        </Card>
    );
} 